import {
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react';

import omit from 'lodash/omit';

import { useAppDispatch } from '../../../../../store/hooks';
import Box from '../../../components/ui/Box';
import SetIsCheckedContextProvider from '../../../components/ui/Table/components/table/context/set-is-checked/set-is-checked.context';
import { withCreateReleaseButton } from '../with-create-release-button/with-create-release-button.hoc';
import { WithFormStateProps } from './models/with-form-state-props.model';
import { WithReleasesLayoutPropsModel } from './models/with-releases-layout-props.model';

export function withReleasesLayout<
  TWrappedWithSearchComponentProps extends WithFormStateProps,
  TWrappedComponentProps extends WithFormStateProps = WithFormStateProps
>(
  WrappedComponent: FunctionComponent<TWrappedComponentProps>
): (
  WrappedWithSearchButton: FunctionComponent<TWrappedWithSearchComponentProps>
) => FunctionComponent<
  WithReleasesLayoutPropsModel<
    TWrappedComponentProps,
    TWrappedWithSearchComponentProps
  >
> {
  return (
      WrappedWithSearchButton: FunctionComponent<TWrappedWithSearchComponentProps>
    ) =>
    (props): ReactElement => {
      const [isCreateFormOpen, setIsCreateFormOpen] = useState<boolean>(false);
      const dispatch = useAppDispatch();

      const WithSearchButtonComponent = useMemo(
        () => withCreateReleaseButton(WrappedWithSearchButton),
        []
      );

      useEffect(() => {
        return () => {
          if (props.wrappedComponentProps?.onReset) {
            dispatch(props.wrappedComponentProps.onReset());
          }
        };
      }, []);

      return (
        <SetIsCheckedContextProvider>
          <Box
            sx={{
              marginTop: '48px',
              overflow: 'hidden',
            }}
          >
            <WithSearchButtonComponent
              {...(props.wrappedWithSearchComponentProps as TWrappedWithSearchComponentProps)}
              isFormOpen={isCreateFormOpen}
              changeFormState={setIsCreateFormOpen}
            />

            <WrappedComponent
              {...(omit(props.wrappedComponentProps, [
                'onReset',
              ]) as TWrappedComponentProps)}
              isFormOpen={isCreateFormOpen}
              changeFormState={setIsCreateFormOpen}
            />
          </Box>
        </SetIsCheckedContextProvider>
      );
    };
}
